import {
  Text,
  GreyGrey30,
  GreyGrey80,
  Primary14SemiBold,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex
} from '@sequencehq/core-components'
import { EmptyContent } from 'modules/Products/Product/components/EmptyContent'
import { ProductListPrice } from 'modules/Products/Product/components/ProductListPrice'
import { priceRowFormatter } from 'modules/Products/formatters'
import { getUniquePrices } from 'lib/getUniquePrices'
import { useMemo, useCallback } from 'react'
import { useLoadAllProductsAndListPrices } from 'Products/hooks/useLoadAllProductsAndListPrices'
import { PriceTableRow } from 'modules/Products/types'
import { useNavigate, useParams } from 'react-router-dom'
import invariant from 'tiny-invariant'
import { NEW_PRICE_PREFIX } from 'common/drawers/PricingEditor/domain/pricingEditor.constants'

export const ProductListPrices = () => {
  const { productId } = useParams<{ productId: string }>()
  invariant(productId, 'productId is required')
  const navigate = useNavigate()

  /**
   * Load the list prices for the product. In order to minimize the impact of the
   * refactoring (in which this cached version was introduced), the loading
   * has been added here. However, if we were to build this from scratch, we'd
   * be accessing this loader/cached more directly from components.
   */
  const { isLoading, allProductsAndListPricesDictionary } =
    useLoadAllProductsAndListPrices()

  const pricesTableData = useMemo(
    () =>
      allProductsAndListPricesDictionary[productId]
        ? getUniquePrices(
            allProductsAndListPricesDictionary[productId]?.listPrices
          ).map(priceRowFormatter)
        : [],
    [allProductsAndListPricesDictionary, productId]
  )

  const handleDuplicate = useCallback(
    (priceRow: PriceTableRow) => {
      // Generate a new unique ID for the duplicated price
      const newPriceId = `${NEW_PRICE_PREFIX}${crypto.randomUUID()}`

      // Navigate to the pricing editor with the duplicated price data
      // The pricing editor will handle loading the original price and creating a copy
      navigate(
        `/products/${productId}/list-price/${newPriceId}?duplicateFrom=${priceRow.id}`
      )
    },
    [navigate, productId]
  )

  return (
    <Flex flexDirection="column">
      <Text {...Primary14SemiBold} color={GreyGrey80}>
        List prices
      </Text>

      {isLoading ? (
        <Flex mt={4}>
          <Skeleton width="100%" height="128px" />
        </Flex>
      ) : (
        <TableContainer
          borderRadius="lg"
          overflow="hidden"
          border={`1px solid ${GreyGrey30}`}
          mt={4}
          width="100%"
        >
          <Table variant="v2" width="100%">
            <Thead>
              <Tr>
                <Th width="25%">Price</Th>
                <Th width="30%">Name</Th>
                <Th>Currency</Th>
                <Th>Frequency</Th>
                <Th>Pricing model</Th>
                <Th>Created</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pricesTableData.length > 0 ? (
                pricesTableData.map(priceRow => (
                  <ProductListPrice
                    key={priceRow.id}
                    priceRow={priceRow}
                    productId={productId}
                    onDuplicate={handleDuplicate}
                  />
                ))
              ) : (
                <Tr
                  sx={{
                    cursor: 'auto!important',
                    _hover: {
                      bgColor: 'inherit!important'
                    }
                  }}
                >
                  <Td colSpan={6}>
                    <EmptyContent priceType="LIST" />
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Flex>
  )
}
